import { Rfx } from "newui/features/Rfx/types/rfx.types";
import {
	AccountSettingsPaths,
	MySuppliersPaths,
	RfxClientPaths,
	RfxProjectPaths,
	SupplierListPaths,
	BaseMemberPaths,
} from "./navigation.types";
import { getSlug } from "helpers/linkHelper";
import { RfxTab } from "newui/features/Rfx/types/component-types";

const rfxProjectBase = "projects";
const supplierListBase = "supplier-list";

export class ClientPaths extends BaseMemberPaths {
	dashboard: string;
	rfxProjects: RfxProjectPaths;
	companies: string;
	supplierSearch: string;
	supplierList: SupplierListPaths;
	mySuppliers: MySuppliersPaths;

	rfx: RfxClientPaths;

	constructor(companyId: number, hasDashboard: boolean, hasRfx: boolean) {
		super(companyId);

		this.dashboard = this.prefixWithBasePath("dashboard");
		this.companies = this.prefixWithBasePath("companies");
		this.rfxProjects = {
			all: this.prefixWithBasePath(`${rfxProjectBase}?tab=allProject`),
			archived: this.prefixWithBasePath(`${rfxProjectBase}?tab=archived`),
			favourites: this.prefixWithBasePath(`${rfxProjectBase}?tab=favourite`),
			my: this.prefixWithBasePath(rfxProjectBase),
			new: this.prefixWithBasePath(`${rfxProjectBase}/new-project`),
		};
		this.supplierSearch = this.prefixWithBasePath("search");

		this.supplierList = {
			my: this.prefixWithBasePath(supplierListBase),
			new: this.prefixWithBasePath(
				this.joinPaths(supplierListBase, "new-supplier-list")
			),
			panel: this.prefixWithBasePath(supplierListBase),
		};

		this.mySuppliers = {
			list: this.prefixWithBasePath("my-suppliers"),
			rate: this.prefixWithBasePath("my-suppliers/rate"),
		};

		this.rfx = {
			all: this.prefixWithBasePath("rfx"),
			// current: this.prefixWithBasePath("rfx/current"),
			// evaluation: this.prefixWithBasePath("rfx/evaluation"),
			// drafts: this.prefixWithBasePath("rfx/drafts"),
			// archived: this.prefixWithBasePath("rfx/archived"),
			createTemplate: this.prefixWithBasePath("rfx-template/new"),
			manageTemplate: this.prefixWithBasePath("manage-templates"),
		};

		if (hasDashboard) {
			this.home = this.dashboard;
		} else if (hasRfx) {
			this.home = this.rfxProjects.all;
		} else {
			this.home = this.supplierSearch;
		}
	}

	// ---------------------------
	// RFx Project Helpers
	// ---------------------------

	rfxProjectsWithType(typeSlug?: string | null) {
		if (typeSlug) {
			return `${this.rfxProjects.my}?type=${typeSlug}`;
		}
		return this.rfxProjects.my;
	}

	newRfxProject(typeSlug?: string | null) {
		if (typeSlug) {
			return `${this.rfxProjects.new}?type=${typeSlug}`;
		}
		return this.rfxProjects.new;
	}

	editRfxProject(rfxProjectId: number) {
		return this.prefixWithBasePath(
			this.joinPaths(rfxProjectBase, `/edit-project/${rfxProjectId}`)
		);
	}

	newRfxProjectWithSupplierList(supplierListId: number) {
		return this.joinPaths(
			this.rfxProjects.new,
			`/suppliers-list/${supplierListId}`
		);
	}

	viewRfxProject(rfxProjectId: number, rfxProjectName: string) {
		const slug = getSlug(rfxProjectName);
		return this.joinPaths(
			this.prefixWithBasePath(rfxProjectBase),
			`/${rfxProjectId}/${slug}`
		);
	}

	// ---------------------------
	// RFx Helpers
	// ---------------------------

	createRfx(rfxId: number) {
		return this.prefixWithBasePath(`rfx/${rfxId}/create-rfx`);
	}

	editRfx(rfxId: number) {
		return this.prefixWithBasePath(`rfx/${rfxId}/edit-rfx`);
	}

	/**
	 * redirects to /account/123/rfx/456/SLUG/OPTIONAL_TAB or All
	 * used to view the entire RFx - with all invites etc.
	 * @param rfxData
	 * @returns
	 */
	viewRfx(rfxData: Rfx, tabId: RfxTab = null, contextId: number | null = null) {
		const slug = getSlug(rfxData?.name);
		const basePath = this.prefixWithBasePath(`rfx/${rfxData?.id}/${slug}`);

		if (!tabId) {
			return `${basePath}/all`;
		}

		return `${basePath}/${tabId}/${contextId ? contextId : ""}`;
	}

	/**
	 * redirects to /account/123/rfx/view
	 * used to view the RFx how a supplier would see it
	 * @param rfxId
	 * @returns
	 */
	viewRfxDetails(rfxId: number) {
		return this.prefixWithBasePath(`rfx/${rfxId}/view`);
	}

	viewEvaluation(rfxId: number, evaluationId: number) {
		return this.prefixWithBasePath(`rfx/${rfxId}/evaluation/${evaluationId}`);
	}

	viewRfxTemplate(rfxTemplateId: number) {
		return this.prefixWithBasePath(`rfx-template/${rfxTemplateId}`);
	}

	/**
	 *
	 * @param supplierListId The id of the supplier list
	 * @param supplierListName the name of the supplier list (for url slug)
	 * @returns
	 */
	viewSupplierList(supplierListId: number, supplierListName: string) {
		const slug = getSlug(supplierListName);
		return this.joinPaths(
			this.prefixWithBasePath(supplierListBase),
			`/${supplierListId}/${slug}`
		);
	}

	// ---------------------------
	// My Suppliers Helpers
	// ---------------------------

	viewSupplierDetail(supplierId: number) {
		return this.joinPaths(
			this.mySuppliers.list,
			`/${supplierId}`
		);
	}

	rateSupplier(supplierId: number) {
		return this.joinPaths(
			this.mySuppliers.rate,
			`/${supplierId}`
		);
	}
}
